// v2/components/RutInscritoInput.jsx
//
// Input de RUT con validación bloqueante de Tarjeta Vecino.
//
// Comportamiento clave (T1.1):
//  - Al perder foco (onBlur) o tras debounce, valida TV.
//  - Si la API responde VIGENTE → tag verde "Tarjeta Vecino vigente"
//  - Si NO_VIGENTE → tag amarillo informativo
//  - Si la API NO RESPONDE → BLOQUEA: dispara onBloqueado(true) y muestra
//    alerta. El componente padre debe deshabilitar "Continuar".

import React, { useEffect, useRef, useState } from 'react';
import { Input, Tag, Alert, Spin } from 'antd';
import { IdcardOutlined, CheckCircleOutlined, WarningOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import useTarjetaVecinoV2 from '../../../hooks/useTarjetaVecinoV2';

/**
 * @param {object} props
 * @param {string} [props.value]
 * @param {(rut: string) => void} [props.onChange]
 * @param {(resultado: object) => void} [props.onResultado] - { esVecino, estado, ... }
 * @param {(bloqueado: boolean) => void} [props.onBloqueado]
 * @param {boolean} [props.required]
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.autoValidar=true] - validar automáticamente al cambiar
 */
export default function RutInscritoInput({
  value,
  onChange,
  onResultado,
  onBloqueado,
  required,
  placeholder = '12345678-9',
  disabled,
  autoValidar = true
}) {
  const { validar, validando, resultado, error, bloqueado, reset } = useTarjetaVecinoV2();
  const [touched, setTouched] = useState(false);
  const debounceRef = useRef(null);
  const lastValidatedRef = useRef('');

  // Notificar cambios al padre
  useEffect(() => { if (onResultado && resultado) onResultado(resultado); }, [resultado, onResultado]);
  useEffect(() => { if (onBloqueado) onBloqueado(bloqueado); }, [bloqueado, onBloqueado]);

  // Auto-validación con debounce 600ms
  useEffect(() => {
    if (!autoValidar) return undefined;
    if (!value || !esRutFormatoValido(value)) {
      reset();
      lastValidatedRef.current = '';
      return undefined;
    }
    if (value === lastValidatedRef.current) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastValidatedRef.current = value;
      validar(value);
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [value, autoValidar, validar, reset]);

  const handleBlur = () => {
    setTouched(true);
    if (!autoValidar && value && esRutFormatoValido(value) && value !== lastValidatedRef.current) {
      lastValidatedRef.current = value;
      validar(value);
    }
  };

  const showFormatError = touched && value && !esRutFormatoValido(value);

  return (
    <div>
      <Input
        size="large"
        prefix={<IdcardOutlined />}
        suffix={validando ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : null}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange?.(e.target.value)}
        onBlur={handleBlur}
        disabled={disabled}
        status={(showFormatError || bloqueado) ? 'error' : undefined}
      />

      {showFormatError && (
        <div style={{ marginTop: 4 }}>
          <small style={{ color: '#ff4d4f' }}>Formato de RUT inválido. Usa 12345678-9.</small>
        </div>
      )}

      {/* Estado VIGENTE */}
      {resultado?.esVecino && !bloqueado && (
        <Tag color="blue" icon={<CheckCircleOutlined />} style={{ marginTop: 8 }}>
          Tarjeta Vecino vigente — aplica descuento
        </Tag>
      )}

      {/* Estado NO_VIGENTE / VECINO_NO_EXISTE */}
      {resultado && !resultado.esVecino && !bloqueado && (
        <Tag color="default" icon={<ExclamationCircleOutlined />} style={{ marginTop: 8 }}>
          {resultado.mensaje || 'Sin descuento de Tarjeta Vecino'}
        </Tag>
      )}

      {/* BLOQUEADO: API TV no responde */}
      {bloqueado && (
        <Alert
          style={{ marginTop: 8 }}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="No se pudo verificar tu Tarjeta Vecino"
          description={
            <>
              <p style={{ margin: 0 }}>{error?.mensaje}</p>
              <p style={{ margin: '8px 0 0 0' }}>
                Por seguridad, no podemos cobrarte sin verificar tu beneficio.
                Por favor intenta de nuevo en unos segundos.
              </p>
            </>
          }
        />
      )}
    </div>
  );
}

function esRutFormatoValido(rut) {
  if (!rut) return false;
  const limpio = String(rut).replace(/\./g, '').trim();
  return /^\d{7,8}-[\dkK]$/.test(limpio);
}
